import { useEffect, useState } from 'react';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import { ChevronDown, TrendingDown, TrendingUp } from 'lucide-react';

const gbp = (v, dp = 0) => `£${(Number(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const DISPLAY = { fontFamily: 'Barlow Condensed, sans-serif' };

export const LiveChip = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-blue-500/15 text-blue-400 border border-blue-500/30" title="Live from the Site Diary / Cost Ledger" data-testid="chip-live">Live</span>
);
export const ManualChip = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-gray-500/15 text-gray-400 border border-gray-500/30" title="Entered manually" data-testid="chip-manual">Manual</span>
);

function Kpi({ label, value, sub, accent = 'border-l-2 border-l-gray-500', primary = false, testid, children }) {
  return (
    <div className={`rounded-lg border border-white/10 bg-[#2A3240] p-4 ${accent}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{label}</div>
      <div className={`${primary ? 'text-3xl' : 'text-2xl'} font-bold text-white mt-1 tabular-nums`} style={DISPLAY}>{value}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-1">{sub}</div>}
      {children}
    </div>
  );
}

function programmeSummary(project) {
  const start = project.programme_start_date || project.start_date;
  const end = project.programme_completion_date || project.completion_date;
  if (!start && !end) return { value: '—', sub: 'no dates set' };
  if (end) {
    const days = differenceInCalendarDays(parseISO(end), new Date());
    return { value: format(parseISO(end), 'dd MMM yyyy'), sub: days >= 0 ? `${days} days to completion` : `${Math.abs(days)} days past completion` };
  }
  return { value: format(parseISO(start), 'dd MMM yyyy'), sub: 'start date' };
}

/** Overview tab: KPI strip + where the money's going + labour planned vs actual. */
export function OverviewTab({ project, finance, summary, labourSummary, invoices, onGoCosts }) {
  const contract = (finance?.contract_value || 0) + (finance?.variations_total || 0);
  const labour = summary?.labour?.total || 0;
  // Materials actual = material + other ledger rows (sundries/skips have no tender line); plant separate
  const material = (summary?.material?.material_only ?? summary?.material?.total ?? 0) + (summary?.other?.total || 0);
  const plant = summary?.plant?.total || 0;
  // Spend = live diary/ledger + manual other actuals (same figure as Finance + Actual vs Tendered)
  const spend = finance?.costs_incurred ?? (labour + material + plant);
  const manualPart = finance?.spend_manual || 0;
  const margin = contract - spend;
  const pct = contract ? Math.min(100, Math.round((spend / contract) * 100)) : 0;
  const invoiced = finance?.invoices_sent ?? (invoices || []).reduce((s, i) => s + (Number(i.total) || 0), 0);
  const planned = labourSummary?.total_cost || 0;
  const prog = programmeSummary(project);
  const b = finance?.breakdown || {};

  // Headline KPI tiles collapse by default (persisted) — consistent with the
  // Projects dashboard. The working money/labour panels below stay visible.
  const [kpiCollapsed, setKpiCollapsed] = useState(() => {
    try { const v = localStorage.getItem('lux.project.kpiCollapsed'); return v === null ? true : v === '1'; } catch { return true; }
  });
  useEffect(() => { try { localStorage.setItem('lux.project.kpiCollapsed', kpiCollapsed ? '1' : '0'); } catch { /* ignore */ } }, [kpiCollapsed]);

  return (
    <div className="space-y-6" data-testid="tab-panel-overview">
      <div data-testid="overview-kpis">
        <button type="button" onClick={() => setKpiCollapsed(v => !v)} className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-wider text-gray-400 hover:text-gray-200 transition-colors" data-testid="overview-kpi-toggle">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${kpiCollapsed ? '-rotate-90' : ''}`} />
          Key figures
        </button>
        {!kpiCollapsed && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Kpi label="Contract value" value={gbp(contract)} sub={finance?.variations_total ? `incl. ${gbp(finance.variations_total)} variations` : 'no approved variations'} testid="kpi-contract-value" />
          <Kpi label="Spend to date" value={gbp(spend)} sub={manualPart > 0 ? `diary + ledger, incl. ${gbp(manualPart)} manual actuals` : 'diary labour + ledger material / plant'} primary accent="border-l-4 border-l-[#F5A623] bg-gradient-to-br from-[#F5A623]/10 to-transparent" testid="kpi-spend-to-date">
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#F5A623] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            <div className="text-[10px] text-gray-500 mt-1">{pct}% of contract</div>
          </Kpi>
          <Kpi label="Margin to date" value={gbp(margin)} sub={`${finance?.profit_margin ?? 0}% · contract − actual spend`} primary accent={`border-l-4 ${margin >= 0 ? 'border-l-emerald-500 bg-gradient-to-br from-emerald-500/10' : 'border-l-red-500 bg-gradient-to-br from-red-500/10'} to-transparent`} testid="kpi-margin-to-date" />
          <Kpi label="Invoiced / still to invoice" value={gbp(invoiced)} sub={`${gbp(Math.max(0, contract - invoiced))} still to invoice`} accent="border-l-2 border-l-blue-400" testid="kpi-invoiced-outstanding" />
          <Kpi label="Programme" value={prog.value} sub={prog.sub} accent="border-l-2 border-l-purple-400" testid="kpi-programme" />
        </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-white/10 bg-[#2A3240] p-5" data-testid="overview-spend-split">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold uppercase tracking-wider text-white" style={DISPLAY}>Where the money's going <span className="text-xs text-gray-500 normal-case tracking-normal font-normal ml-1" data-testid="overview-spend-basis">ex VAT · like the tender</span></h2>
            <button type="button" onClick={onGoCosts} className="text-xs text-[#F5A623] hover:underline" data-testid="overview-open-costs">Open Costs →</button>
          </div>
          {[
            ['Labour (employee diary + invoiced)', labour, b.labour, '#F5A623', 'labour'],
            ['Materials & other (ledger)', material, b.materials, '#60A5FA', 'materials'],
            ['Plant & fuel (ledger)', plant, b.plant, '#C084FC', 'plant'],
          ].map(([label, actual, tendered, colour, key]) => {
            const p = tendered ? Math.min(100, Math.round((actual / tendered) * 100)) : 0;
            return (
              <div key={label} className="py-2.5 border-b border-white/5 last:border-0" data-testid={`spend-split-${key}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-200 inline-flex items-center gap-2">{label}<LiveChip /></span>
                  <span className="tabular-nums text-white font-semibold">{gbp(actual, 2)} <span className="text-gray-500 font-normal">/ {gbp(tendered)}</span></span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full transition-[width] duration-500" style={{ width: `${p}%`, background: actual > (tendered || 0) && tendered ? '#F87171' : colour }} /></div>
              </div>
            );
          })}
        </div>
        <div className="rounded-lg border border-white/10 bg-[#2A3240] p-5 space-y-3" data-testid="overview-labour-drift">
          <h2 className="text-lg font-semibold uppercase tracking-wider text-white" style={DISPLAY}>Labour · planned vs actual</h2>
          <div className="flex items-end justify-between"><span className="text-xs text-gray-400">Planned (schedule)</span><span className="text-xl font-bold text-gray-200 tabular-nums" style={DISPLAY}>{gbp(planned)}</span></div>
          <div className="flex items-end justify-between"><span className="text-xs text-gray-400">Actual (counted)</span><span className="text-xl font-bold text-[#F5A623] tabular-nums" style={DISPLAY}>{gbp(labour)}</span></div>
          <div className="text-[11px] text-gray-500 space-y-0.5" data-testid="overview-labour-split">
            <div className="flex justify-between text-emerald-300/90" data-testid="overview-labour-diary-all-row"><span>On site · diary running total (everyone at day rate)</span><span className="tabular-nums font-semibold" data-testid="overview-labour-diary-all">{gbp(summary?.labour?.diary_all || 0)}</span></div>
            <div className="flex justify-between"><span>Employees · diary days</span><span className="tabular-nums text-gray-300" data-testid="overview-labour-employee-diary">{gbp(summary?.labour?.diary || 0)}</span></div>
            <div className="flex justify-between"><span>Confirmed · invoiced (Cost Inbox / manual)</span><span className="tabular-nums text-gray-300" data-testid="overview-labour-invoiced">{gbp(summary?.labour?.invoiced ?? summary?.labour?.ledger ?? 0)}</span></div>
            {(summary?.labour?.awaiting_invoice || 0) > 0 && (
              <div className="flex justify-between text-amber-400/90" data-testid="overview-labour-awaiting">
                <span>Sub-contractor diary days · not yet invoiced (in running total, not counted)</span><span className="tabular-nums">{gbp(summary.labour.awaiting_invoice)}</span>
              </div>
            )}
          </div>
          <div className={`text-xs inline-flex items-center gap-1 ${labour <= planned ? 'text-emerald-400' : 'text-red-400'}`} data-testid="overview-labour-drift-value">
            {labour <= planned ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
            {gbp(Math.abs(planned - labour))} {labour <= planned ? 'under' : 'over'} plan
          </div>
          {summary?.labour?.total_missing_rate > 0 && (
            <div className="text-[11px] text-amber-400" data-testid="overview-missing-rates">{summary.labour.total_missing_rate} person-days have no day rate</div>
          )}
        </div>
      </div>
    </div>
  );
}
